/**
 * Freeze Reconciler
 *
 * Walks the user's daily activity and existing event log, emits new
 * `fired` and `refused` freeze events for any unsaved missed day.
 * Idempotent: re-running on the same data produces no new events.
 *
 * Rules (see STORE_SYSTEM.md):
 *   - Normal freeze covers a single missed day; cannot fire on a day
 *     immediately following another day saved by a normal freeze.
 *   - Weekend freeze fires only when BOTH days of the user's configured
 *     weekend window are missed; covers both at once.
 *   - If a freeze type was equipped but blocked by a rule, emit a
 *     `refused` event so the banner can explain.
 */

import type { DailyActivity } from '../types/gamification';
import type { FreezeEvent, FreezeType, WeekendWindow } from '../types/store';
import {
  appendFreezeEvent,
  getFreezeEvents,
  getStoreSettings,
  getTmEvents,
  newFreezeEventId,
} from '../services/StoreStateService';

const LOOKBACK_DAYS = 60;

// [first, second] day-of-week indices, in calendar order (Sun = 0).
function getWeekendDays(window: WeekendWindow): [number, number] {
  switch (window) {
    case 'fri-sat':
      return [5, 6]; // Fri, Sat
    case 'sat-sun':
      return [6, 0]; // Sat, Sun
    default: {
      // An unrecognised/missing value must not silently mean sat-sun — that
      // shifted coverage onto the wrong days for fri-sat users.
      console.warn(`Unknown weekend window "${window}", falling back to sat-sun`);
      return [6, 0];
    }
  }
}

function dayOfWeek(dateStr: string): number {
  return new Date(dateStr + 'T00:00:00').getDay();
}

function isWeekendDay(dateStr: string, window: WeekendWindow): boolean {
  const day = dayOfWeek(dateStr);
  const [a, b] = getWeekendDays(window);
  return day === a || day === b;
}

function getOtherWeekendDay(dateStr: string, window: WeekendWindow): string {
  const [a] = getWeekendDays(window);
  const day = dayOfWeek(dateStr);
  const offset = day === a ? 1 : -1;
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export interface ReconcileResult {
  newFired: FreezeEvent[];
  newRefused: FreezeEvent[];
}

export async function reconcileFreezeEvents(
  userId: string,
  dailyActivities: DailyActivity[],
  levelsRequiredPerDay: number,
): Promise<ReconcileResult> {
  const settings = await getStoreSettings(userId);
  const existingFreezeEvents = await getFreezeEvents(userId);
  const tmEvents = await getTmEvents(userId);

  const savedDays = new Set<string>();
  const savedByNormal = new Set<string>();
  for (const e of existingFreezeEvents) {
    if (e.type === 'fired') {
      for (const d of e.savedDays) {
        savedDays.add(d);
        if (e.freezeType === 'normal') savedByNormal.add(d);
      }
    }
  }
  for (const e of tmEvents) {
    if (e.type === 'repaired') savedDays.add(e.repairedDay);
  }

  // Refusals are tracked per freeze type. A day refused by one type must
  // still be evaluated for the other, and must be re-evaluated for the same
  // type once inventory changes — otherwise a single refusal permanently
  // seals a day against any later coverage.
  const refusedByType = new Map<string, Set<FreezeType>>();
  const isRefused = (date: string, freezeType: FreezeType): boolean =>
    refusedByType.get(date)?.has(freezeType) ?? false;
  const markRefused = (date: string, freezeType: FreezeType): void => {
    const forDay = refusedByType.get(date) ?? new Set<FreezeType>();
    forDay.add(freezeType);
    refusedByType.set(date, forDay);
  };
  for (const e of existingFreezeEvents) {
    if (e.type === 'refused') markRefused(e.missedDay, e.freezeType);
  }

  const activeDays = new Set<string>();
  for (const day of dailyActivities) {
    if (day.totalLevels >= levelsRequiredPerDay) activeDays.add(day.date);
  }

  // A freeze can fire on any missed day from its purchase day onward,
  // inclusive: buying a freeze on the day you lapse should still save that
  // day. It cannot reach back to gaps that predate the purchase. We track
  // per-day eligibility by the YYYY-MM-DD portion of each purchase's ISO
  // timestamp.
  const tsToLocalDate = (ts: string): string => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const purchasedNormalDays = existingFreezeEvents
    .filter(e => e.type === 'purchased' && e.freezeType === 'normal')
    .map(e => tsToLocalDate(e.ts))
    .sort();
  const purchasedWeekendDays = existingFreezeEvents
    .filter(e => e.type === 'purchased' && e.freezeType === 'weekend')
    .map(e => tsToLocalDate(e.ts))
    .sort();

  // Total fires already in the log (consumed inventory). We increment
  // these as we fire new events during this reconcile pass, so the
  // per-day availability accounting stays consistent.
  let firedNormalSoFar = existingFreezeEvents.filter(
    e => e.type === 'fired' && e.freezeType === 'normal',
  ).length;
  let firedWeekendSoFar = existingFreezeEvents.filter(
    e => e.type === 'fired' && e.freezeType === 'weekend',
  ).length;

  const normalAvailableOn = (date: string): number =>
    purchasedNormalDays.filter(d => d <= date).length - firedNormalSoFar;
  const weekendAvailableOn = (date: string): number =>
    purchasedWeekendDays.filter(d => d <= date).length - firedWeekendSoFar;

  const today = new Date();
  const dates: string[] = [];
  for (let i = LOOKBACK_DAYS; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    dates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  const todayStr = dates[dates.length - 1];

  const newFired: FreezeEvent[] = [];
  const newRefused: FreezeEvent[] = [];

  let prevDaySavedByNormal = false;

  for (const date of dates) {
    if (date === todayStr) continue;

    if (activeDays.has(date)) {
      prevDaySavedByNormal = false;
      continue;
    }
    if (savedDays.has(date)) {
      prevDaySavedByNormal = savedByNormal.has(date);
      continue;
    }
    // Uncovered missed day. Try weekend first, then normal. Days that were
    // refused on an earlier pass are retried — inventory may have changed.

    if (isWeekendDay(date, settings.weekendWindow) && weekendAvailableOn(date) > 0) {
      const otherDay = getOtherWeekendDay(date, settings.weekendWindow);
      // A previously-refused day is still a missed day, so it counts toward
      // the both-days-missed requirement. Today (and anything later) is not a
      // missed day yet — it's still in progress — so a weekend freeze must not
      // spend half its coverage on it. Defer to a later reconcile instead.
      const otherMissed =
        otherDay < todayStr &&
        !activeDays.has(otherDay) &&
        !savedDays.has(otherDay);

      if (otherMissed) {
        const event: FreezeEvent = {
          id: newFreezeEventId(),
          type: 'fired',
          freezeType: 'weekend',
          ts: new Date().toISOString(),
          savedDays: [date, otherDay].sort(),
        };
        newFired.push(event);
        savedDays.add(date);
        savedDays.add(otherDay);
        firedWeekendSoFar++;
        prevDaySavedByNormal = false;
        continue;
      }
    }

    if (normalAvailableOn(date) > 0 && !prevDaySavedByNormal) {
      const event: FreezeEvent = {
        id: newFreezeEventId(),
        type: 'fired',
        freezeType: 'normal',
        ts: new Date().toISOString(),
        savedDays: [date],
      };
      newFired.push(event);
      savedDays.add(date);
      savedByNormal.add(date);
      firedNormalSoFar++;
      prevDaySavedByNormal = true;
      continue;
    }

    if (
      normalAvailableOn(date) > 0 &&
      prevDaySavedByNormal &&
      !isRefused(date, 'normal')
    ) {
      const event: FreezeEvent = {
        id: newFreezeEventId(),
        type: 'refused',
        freezeType: 'normal',
        ts: new Date().toISOString(),
        missedDay: date,
        refusalReason: 'consecutive_day',
      };
      newRefused.push(event);
      markRefused(date, 'normal');
    } else if (
      weekendAvailableOn(date) > 0 &&
      isWeekendDay(date, settings.weekendWindow) &&
      getOtherWeekendDay(date, settings.weekendWindow) < todayStr &&
      !isRefused(date, 'weekend')
    ) {
      const event: FreezeEvent = {
        id: newFreezeEventId(),
        type: 'refused',
        freezeType: 'weekend',
        ts: new Date().toISOString(),
        missedDay: date,
        refusalReason: 'single_weekend_day',
      };
      newRefused.push(event);
      markRefused(date, 'weekend');
    }

    prevDaySavedByNormal = false;
  }

  for (const e of [...newFired, ...newRefused]) {
    try {
      await appendFreezeEvent(userId, e);
    } catch (error) {
      console.error('Failed to append reconciler event:', error);
    }
  }

  return { newFired, newRefused };
}
